/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Row, Col } from "react-awesome-styled-grid";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Table, Spacer, Tag, Text, theme, Tooltip } from "@nordcloud/gnui";
import { TagWrap } from "~/components/Applications/ApplicationItem";
import { CollapseItem } from "~/components/CollapseItem/CollapseItem";
import FlexContainer from "~/components/Styled/FlexContainer";
import { dateFormatWithHours } from "~/constants";
import { useCustomer } from "~/context/User/Customer";
import { ROUTES } from "~/routing";
import {
  displayCurrencySymbol,
  displayServiceHours,
  displaySlaType,
  displaySlaUnitType,
} from "~/tools";
import { StyledOutsideLink } from "../Styled/Common";
import { TooltipTitle } from "../Styled/TooltipTitle";
import { EditProductPricing } from "./EditProductPricing";
import { EditProductSla } from "./EditProductSla";
import { UnassignProduct } from "./ProductUnassign";
import { AssignedProduct, AttachedProductSlas } from "./types";

type ProductItemProps = {
  item: AssignedProduct;
  refetch: () => void;
  canEdit?: boolean;
};
const StyledMaxWidthWrapper = styled(FlexContainer)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-size: 0.8rem;
`;

function ProductItem({
  item: { product, attachedSlas },
  refetch,
  canEdit = true,
}: ProductItemProps) {
  const [isEmpty, setIsEmpty] = React.useState(true);
  const { customer } = useCustomer();

  React.useEffect(() => {
    attachedSlas.length === 0 &&
    product.pricing?.length === 0 &&
    !product.fixedFee &&
    !product.currency
      ? setIsEmpty(true)
      : setIsEmpty(false);
  }, [attachedSlas, product, setIsEmpty]);

  return (
    <CollapseItem
      buttons={
        <>
          {canEdit && (
            <>
              <UnassignProduct product={product} refetch={refetch} />
              <EditProductPricing product={product} refetch={refetch} />
            </>
          )}
        </>
      }
      name={
        <FlexContainer>
          <Link to={`${ROUTES.productCatalog.index}/${product?.id}`}>
            {product?.name}
          </Link>
        </FlexContainer>
      }
    >
      {!isEmpty && (
        <div>
          <FlexContainer
            css={{ margin: "1rem 0 0 0.8rem" }}
            align="flex-start"
            justify="flex-start"
          >
            <Text tag="div" color={theme.color.text.text02}>
              Fixed Fee:
            </Text>
            <Text weight="medium" css={{ margin: "0 1rem 0 1rem" }}>
              {product.fixedFee ? product.fixedFee : "-"}{" "}
              {displayCurrencySymbol(customer?.info?.currency)}
            </Text>
            <TooltipTitle
              title="Pricing:"
              caption="Link to a spreadsheet with pricing formula for this Customer"
              minWidth="3rem"
            />
            {product.pricing.length === 0 ? (
              "-"
            ) : (
              <StyledOutsideLink href={product.pricing}>Link</StyledOutsideLink>
            )}
            <Text
              tag="div"
              css={{ margin: "0 0 0 1rem" }}
              color={theme.color.text.text02}
            >
              Currency:
            </Text>
            <Text weight="medium" css={{ margin: "0 1rem 0 1rem" }}>
              {product.effectiveCurrency}{" "}
              {displayCurrencySymbol(product?.effectiveCurrency)}
            </Text>
          </FlexContainer>
          {attachedSlas.length !== 0 && (
            <Row>
              <Col>
                <Spacer height="1rem" />
                <Table small>
                  <Table.thead>
                    <Table.tr>
                      <Table.th>SLA Name</Table.th>
                      <Table.th>Description</Table.th>
                      <Table.th>Type</Table.th>
                      <Table.th>Unit Type</Table.th>
                      <Table.th>Value</Table.th>
                      <Table.th>Unit</Table.th>
                      <Table.th>Support service hours</Table.th>
                      <Table.th>Priority</Table.th>
                      <Table.th>Comment</Table.th>
                      <Table.th>Modified</Table.th>
                      {canEdit && (
                        <Table.th css={{ textAlign: "center" }}>
                          Action
                        </Table.th>
                      )}
                    </Table.tr>
                  </Table.thead>
                  <Table.tbody>
                    {attachedSlas?.map((item: AttachedProductSlas) => {
                      return (
                        <Table.tr key={item?.sla?.id}>
                          <Table.td>{item?.sla?.name}</Table.td>
                          <Table.td>{item?.sla?.description}</Table.td>
                          <Table.td>
                            {displaySlaType(item?.sla?.slaType)}
                          </Table.td>
                          <Table.td>
                            {displaySlaUnitType(item?.sla?.unitType)}
                          </Table.td>
                          <Table.td>{item?.effectiveValue}</Table.td>
                          <Table.td>
                            <TagWrap>
                              <Tag text={item?.sla?.range?.unit} />
                            </TagWrap>
                          </Table.td>
                          <Table.td>
                            {displayServiceHours(item?.sla?.supportHours)}
                          </Table.td>
                          <Table.td>{item?.sla?.priority || "-"}</Table.td>
                          <Table.td>{item?.data?.comment}</Table.td>
                          <Table.td css={{ position: "relative" }}>
                            <Tooltip
                              css={{ width: "10rem" }}
                              caption={
                                <FlexContainer>
                                  <Text
                                    size="sm"
                                    mb={0}
                                    mr={theme.spacing.spacing01}
                                  >
                                    By:
                                  </Text>
                                  <Text
                                    size="sm"
                                    weight="medium"
                                    mb={0}
                                    mr={theme.spacing.spacing01}
                                  >
                                    {item?.data?.log?.modified?.author}
                                  </Text>
                                </FlexContainer>
                              }
                              status="notification"
                            >
                              <StyledMaxWidthWrapper>
                                {item?.data?.log?.modified?.time &&
                                  dayjs(item?.data.log?.modified?.time).format(
                                    `${dateFormatWithHours}`
                                  )}
                              </StyledMaxWidthWrapper>
                            </Tooltip>
                          </Table.td>
                          {canEdit && (
                            <Table.td hasLeftBorder>
                              <EditProductSla
                                refetch={refetch}
                                val={item?.effectiveValue || ""}
                                comm={item?.data?.comment as string}
                                sla={item?.sla}
                                productId={product?.id}
                              />
                            </Table.td>
                          )}
                        </Table.tr>
                      );
                    })}
                  </Table.tbody>
                </Table>
              </Col>
            </Row>
          )}
        </div>
      )}
    </CollapseItem>
  );
}

export { ProductItem };
